import { Injectable } from "@angular/core";
import { Answer } from "@domain/Answer";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { Exercise } from "@domain/Exercise";
import { HomophoneGroup } from "@domain/HomophoneGroup";
import { RewardCursor, User } from "@domain/User";

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
declare let gtag: Function;

export enum RewardCursorSelectionSource {
  // eslint-disable-next-line no-unused-vars
  UnlockedModal,
  // eslint-disable-next-line no-unused-vars
  SelectionModal,
}

const PROJECT_NAME = "ehomophones";

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private trackEvent(eventName: string, params: Record<string, unknown> = {}) {
    gtag("event", eventName, { project_name: PROJECT_NAME, ...params });
  }

  setUserId(user: User) {
    gtag("set", { user_id: user.id });
  }

  removeUserId() {
    gtag("set", { user_id: null });
  }

  trackExerciserStart() {
    this.trackEvent("ehomophones_start");
  }

  trackSequenceStart(educationLevelGroup: EducationLevelGroup, homophoneGroups: HomophoneGroup[], isInErrorReworkMode: boolean) {
    this.trackEvent("ehomophones_exercice_start", {
      groupe_niveau_scolaire: educationLevelGroup.name,
      groupes_homophones: this.getHomophoneGroupNames(homophoneGroups),
      reponse_retravaillee: isInErrorReworkMode,
    });
  }

  trackSequenceCompletion(
    educationLevelGroup: EducationLevelGroup,
    homophoneGroups: HomophoneGroup[],
    numberOfCorrectAnswers: number,
    numberOfIncorrectAnswers: number,
    isInErrorReworkMode: boolean
  ) {
    this.trackEvent("ehomophones_exercice_finish", {
      groupe_niveau_scolaire: educationLevelGroup.name,
      groupes_homophones: this.getHomophoneGroupNames(homophoneGroups),
      nombre_bonnes_reponses: numberOfCorrectAnswers,
      nombre_mauvaises_reponses: numberOfIncorrectAnswers,
      mode_retravailler_erreur: isInErrorReworkMode,
    });
  }

  trackSequenceAbort(educationLevelGroup: EducationLevelGroup, homophoneGroups: HomophoneGroup[], numberOfCompletedExercises = 0) {
    this.trackEvent("ehomophones_exercice_quit", {
      groupe_niveau_scolaire: educationLevelGroup.name,
      groupes_homophones: this.getHomophoneGroupNames(homophoneGroups),
      nombre_questions_completees: numberOfCompletedExercises,
    });
  }

  trackNewSequenceStart(educationLevelGroup: EducationLevelGroup, homophoneGroups: HomophoneGroup[]) {
    this.trackEvent("ehomophones_continue_practice", {
      groupe_niveau_scolaire: educationLevelGroup.name,
      groupes_homophones: this.getHomophoneGroupNames(homophoneGroups),
    });
  }

  trackExerciseEnd(educationLevelGroup: EducationLevelGroup, homophoneGroups: HomophoneGroup[]) {
    this.trackEvent("ehomophones_end_practice", {
      groupe_niveau_scolaire: educationLevelGroup.name,
      groupes_homophones: this.getHomophoneGroupNames(homophoneGroups),
    });
  }

  trackAnswerSubmitted(
    educationLevelGroup: EducationLevelGroup,
    homophoneGroups: HomophoneGroup[],
    exercise: Exercise,
    answer: Answer,
    numberOfClicksOnPlayAudioButton: number,
    isInErrorReworkMode: boolean
  ) {
    const eventName = answer.isCorrect() ? "ehomophones_good_answer" : "ehomophones_wrong_answer";

    this.trackEvent(eventName, {
      id_question: exercise.getCorrectSentence(),
      groupe_niveau_scolaire: educationLevelGroup.name,
      groupes_homophones: this.getHomophoneGroupNames(homophoneGroups),
      reponse_envoyee: exercise.getCompletedSentence(answer),
      reponse_attendue: exercise.getCorrectSentence(),
      nombre_ecoutes: numberOfClicksOnPlayAudioButton,
      reponse_retravaillee: isInErrorReworkMode,
    });
  }

  trackHearWord(word: string, homophoneGroups: HomophoneGroup[], location: string) {
    gtag("event", "ehomophones_hear_word", {
      mot: word,
      homophones_selectionnes: this.getHomophoneGroupNames(homophoneGroups),
      emplacement: location,
    });
  }

  trackNewRewardCursorUnlocked(educationLevelGroup: EducationLevelGroup, unlockedRewardCursor: RewardCursor) {
    this.trackEvent("ehomophones_reward_received", {
      id_recompense: unlockedRewardCursor,
      groupe_niveau_scolaire: educationLevelGroup.name,
    });
  }

  trackRewardCursorMaintained(educationLevelGroup: EducationLevelGroup, rewardCursorMaintained?: RewardCursor) {
    this.trackEvent("ehomophones_reward_maintained", {
      id_recompense: rewardCursorMaintained ?? "aucune récompense",
      groupe_niveau_scolaire: educationLevelGroup.name,
    });
  }

  trackRewardCursorSelection(
    educationLevelGroup: EducationLevelGroup,
    selectedRewardCursor: RewardCursor,
    sourceOfSelection: RewardCursorSelectionSource
  ) {
    const mappedSourceOfSelection =
      sourceOfSelection === RewardCursorSelectionSource.SelectionModal ? "Modale de sélection" : "Modale de déverouillage";

    this.trackEvent("ehomophones_reward_used", {
      id_recompense: selectedRewardCursor,
      groupe_niveau_scolaire: educationLevelGroup.name,
      emplacement_selection: mappedSourceOfSelection,
    });
  }

  trackRewardVisibilityToggle(selectedRewardCursor: RewardCursor, rewardMechanismShown: boolean, educationLevelGroup?: EducationLevelGroup) {
    this.trackEvent("ehomophones_reward_toggle", {
      id_recompense: selectedRewardCursor,
      groupe_niveau_scolaire: educationLevelGroup?.name ?? "groupe de niveau scolaire non sélectionné",
      statut: rewardMechanismShown ? "affiché" : "masqué",
    });
  }

  trackFullScreenOn() {
    this.trackEvent("ehomophones_fullscreen_on");
  }

  trackFullScreenOff() {
    this.trackEvent("ehomophones_fullscreen_off");
  }

  trackException(error: Error) {
    const fullStack = error.stack;
    const stackLines = fullStack ? fullStack.split("\n") : [];
    const firstFiveCallFrames = stackLines.slice(0, 5);

    this.trackEvent("ehomophones_error", {
      message: error.message,
      stacktract: firstFiveCallFrames.join("\n"),
    });
  }

  private getHomophoneGroupNames(homophoneGroups: HomophoneGroup[]): string {
    return homophoneGroups.map(group => group.displayName).join(" | ");
  }
}
